import { useState } from 'react'
import api from '../../api/axios'

const STEPS = ['Conexão', 'Revisar Clientes', 'Importar']
const PER_PAGE = 15

export default function ImportacaoClientes() {
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')

  // Passo 0
  const [creds, setCreds] = useState({ email: '', password: '' })

  // Passo 1
  const [clientes, setClientes] = useState([])
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [expandido, setExpandido] = useState(null)       // id_antigo expandido
  const [detalhe, setDetalhe] = useState({})             // { id_antigo: { detalhes, fazendas } }
  const [loadingDetalhe, setLoadingDetalhe] = useState(null)

  // Passo 2
  const [organizadores, setOrganizadores] = useState([])
  const [orgSelecionada, setOrgSelecionada] = useState(null)
  const [loadingOrg, setLoadingOrg] = useState(false)
  const [buscaOrg, setBuscaOrg] = useState('')
  const [resultado, setResultado] = useState(null)
  const [dryRunOk, setDryRunOk] = useState(false)
  const [atualizarExistentes, setAtualizarExistentes] = useState(false)

  // Job background
  const [jobId, setJobId] = useState(null)
  const [jobFase, setJobFase] = useState(null)
  const [jobFaseLabel, setJobFaseLabel] = useState('')
  const [jobProgresso, setJobProgresso] = useState({ concluidos: 0, total: 0 })

  // ── Passo 0 ───────────────────────────────────────────────────────────────
  async function handleBuscarClientes() {
    setErro('')
    if (!creds.email || !creds.password) { setErro('Preencha e-mail e senha.'); return }
    setLoading(true)
    try {
      const { data } = await api.post('/super-admin/importacao/buscar-clientes', creds, { timeout: 120000 })
      setClientes(data.clientes || [])
      setPagina(1)
      setBusca('')
      setExpandido(null)
      setDetalhe({})
      setStep(1)
      setLoadingOrg(true)
      const { data: orgsData } = await api.get('/organizadores?per_page=100&status=ativo')
      setOrganizadores(orgsData.data || [])
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao conectar ao sistema antigo.')
    } finally {
      setLoading(false)
      setLoadingOrg(false)
    }
  }

  // ── Expandir linha — busca detalhe on-demand ──────────────────────────────
  async function toggleDetalhe(id_antigo) {
    if (expandido === id_antigo) { setExpandido(null); return }
    setExpandido(id_antigo)
    if (detalhe[id_antigo]) return  // já carregado
    setLoadingDetalhe(id_antigo)
    try {
      const { data } = await api.post('/super-admin/importacao/detalhe-cliente', {
        ...creds,
        cliente_id: id_antigo,
      })
      setDetalhe(prev => ({ ...prev, [id_antigo]: data }))
    } catch {
      setDetalhe(prev => ({ ...prev, [id_antigo]: { erro: 'Falha ao carregar' } }))
    } finally {
      setLoadingDetalhe(null)
    }
  }

  // ── Passo 2 ───────────────────────────────────────────────────────────────
  async function handleDryRun() {
    setErro(''); setLoading(true); setResultado(null)
    try {
      const { data } = await api.post('/super-admin/importacao/executar-clientes',
        { ...creds, org_id: orgSelecionada.id, dry_run: true, atualizar_existentes: atualizarExistentes },
        { timeout: 60_000 }   // dry-run só busca lista: 60s suficiente
      )
      setResultado(data); setDryRunOk(true)
    } catch (e) {
      const detail = e.response?.data?.detail
      setErro(typeof detail === 'string' ? detail : JSON.stringify(detail) || `Erro ${e.response?.status || 'timeout'}: verifique os logs do backend.`)
    } finally { setLoading(false) }
  }

  async function handleImportar() {
    setErro(''); setLoading(true); setResultado(null)
    setJobId(null); setJobFase(null); setJobFaseLabel(''); setJobProgresso({ concluidos: 0, total: 0 })
    try {
      const { data } = await api.post('/super-admin/importacao/executar-clientes',
        { ...creds, org_id: orgSelecionada.id, dry_run: false, atualizar_existentes: atualizarExistentes },
        { timeout: 15_000 }
      )

      const id = data.job_id
      setJobId(id)
      setJobProgresso({ concluidos: 0, total: data.a_importar || 0 })

      const poll = async () => {
        try {
          const { data: job } = await api.get(`/super-admin/importacao/status-leiloes-bulk/${id}`)
          setJobFase(job.fase)
          setJobFaseLabel(job.fase_label || '')
          if (job.scraping_concluidos != null) {
            setJobProgresso({ concluidos: job.scraping_concluidos, total: job.a_importar || 0 })
          }

          if (job.status === 'concluido') {
            setResultado({
              dry_run: false,
              total_scraped:       job.total_scraped || 0,
              duplicatas:          job.duplicatas || 0,
              importados:          job.importados || 0,
              atualizados:         job.atualizados || 0,
              fazendas_importadas: job.fazendas_importadas || 0,
              bancos_importados:   job.bancos_importados || 0,
              erros:               job.erros || [],
            })
            setDryRunOk(false)
            setLoading(false)
          } else if (job.status === 'erro') {
            setErro(job.erro_global || 'Erro na importação')
            setLoading(false)
          } else {
            setTimeout(poll, 2500)
          }
        } catch {
          setTimeout(poll, 3000)
        }
      }
      poll()

    } catch (e) {
      const detail = e.response?.data?.detail
      setErro(typeof detail === 'string' ? detail : JSON.stringify(detail) || `Erro ${e.response?.status || 'desconhecido'}`)
      setLoading(false)
    }
  }

  // ── Filtro + paginação ────────────────────────────────────────────────────
  const filtrados = clientes.filter(c =>
    !busca ||
    c.nome.toLowerCase().includes(busca.toLowerCase()) ||
    (c.cpf_cnpj || '').includes(busca) ||
    (c.cidade || '').toLowerCase().includes(busca.toLowerCase()) ||
    (c.fazendas_nomes || []).some(f => f.toLowerCase().includes(busca.toLowerCase()))
  )
  const totalPaginas = Math.ceil(filtrados.length / PER_PAGE)
  const paginaAtual = Math.min(pagina, totalPaginas || 1)
  const visíveis = filtrados.slice((paginaAtual - 1) * PER_PAGE, paginaAtual * PER_PAGE)

  function handleBusca(v) { setBusca(v); setPagina(1) }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Importação de Clientes</h1>
      <p className="text-sm text-gray-500 mb-6">
        Exclusivo SuperAdmin — importa produtores/clientes do sistema antigo (app.rematei.com.br)
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-3 mb-8">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`flex items-center gap-2 ${i <= step ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold
                ${i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                {i + 1}
              </div>
              <span className={`text-sm font-medium ${i === step ? 'text-blue-600' : ''}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-12 h-px bg-gray-200" />}
          </div>
        ))}
      </div>

      {erro && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{erro}</div>
      )}

      {/* ── Passo 0 ── */}
      {step === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-md">
          <h2 className="font-semibold text-gray-800 mb-4">Credenciais do sistema antigo</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" value={creds.email} onChange={e => setCreds(p => ({ ...p, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="seu@email.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input type="password" value={creds.password} onChange={e => setCreds(p => ({ ...p, password: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleBuscarClientes()}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={handleBuscarClientes} disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
              {loading ? 'Buscando clientes...' : 'Buscar Clientes →'}
            </button>
          </div>
        </div>
      )}

      {/* ── Passo 1: Revisar ── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-semibold text-gray-800">{clientes.length} clientes encontrados</span>
              <span className="text-sm text-gray-400 ml-2">· {filtrados.length} exibidos · clique na linha para ver detalhes</span>
            </div>
            <button onClick={() => setStep(2)}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
              Continuar →
            </button>
          </div>

          <input type="text" value={busca} onChange={e => handleBusca(e.target.value)}
            placeholder="Buscar por nome, CPF, cidade ou fazenda..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left">
                  <th className="px-4 py-3 font-medium text-gray-500 w-8">#</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Nome</th>
                  <th className="px-4 py-3 font-medium text-gray-600">CPF/CNPJ</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Cidade</th>
                  <th className="px-4 py-3 font-medium text-gray-600">UF</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Telefone</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Fazendas</th>
                  <th className="px-4 py-3 font-medium text-gray-600 w-16 text-center">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {visíveis.map((c, i) => {
                  const idx = (paginaAtual - 1) * PER_PAGE + i + 1
                  const isOpen = expandido === c.id_antigo
                  const det = detalhe[c.id_antigo]
                  return (
                    <>
                      <tr key={c.id_antigo}
                        className={`border-b border-gray-100 transition-colors cursor-pointer
                          ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleDetalhe(c.id_antigo)}>
                        <td className="px-4 py-3 text-gray-400 text-xs">{idx}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{c.nome}</td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.cpf_cnpj || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{c.cidade || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{c.estado || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{c.telefone || '—'}</td>
                        <td className="px-4 py-3">
                          {(c.fazendas_nomes || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {c.fazendas_nomes.map((f, fi) => (
                                <span key={fi} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs border border-emerald-100">{f}</span>
                              ))}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-medium transition-colors ${isOpen ? 'text-blue-600' : 'text-gray-400'}`}>
                            {loadingDetalhe === c.id_antigo ? '⏳' : isOpen ? '▲' : '▼'}
                          </span>
                        </td>
                      </tr>

                      {/* Linha expandida */}
                      {isOpen && (
                        <tr key={`${c.id_antigo}-det`} className="bg-blue-50 border-b border-blue-100">
                          <td colSpan={8} className="px-6 py-4">
                            {!det && loadingDetalhe === c.id_antigo && (
                              <p className="text-sm text-gray-400">Carregando detalhes...</p>
                            )}
                            {det?.erro && <p className="text-sm text-red-500">{det.erro}</p>}
                            {det && !det.erro && (
                              <div className="grid grid-cols-2 gap-6">
                                {/* Dados principais */}
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dados Principais</p>
                                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                    {[
                                      ['E-mail',         det.detalhes?.email],
                                      ['RG/IE',          det.detalhes?.rg_ie],
                                      ['Insc. Estadual', det.detalhes?.inscricao_estadual],
                                      ['Telefone 2',     det.detalhes?.telefone2],
                                      ['Endereço',       det.detalhes?.endereco],
                                      ['Número',         det.detalhes?.numero],
                                      ['Bairro',         det.detalhes?.bairro],
                                      ['CEP',            det.detalhes?.cep],
                                      ['Sócio',          det.detalhes?.is_socio ? 'Sim' : 'Não'],
                                      ['Ativo',          det.detalhes?.is_active ? 'Sim' : 'Não'],
                                    ].map(([label, val]) => val ? (
                                      <div key={label} className="flex gap-1">
                                        <span className="text-gray-400 flex-shrink-0">{label}:</span>
                                        <span className="text-gray-700 font-medium">{val}</span>
                                      </div>
                                    ) : null)}
                                  </div>
                                  {det.detalhes?.observacoes && (
                                    <div className="mt-2 text-sm">
                                      <span className="text-gray-400">Obs: </span>
                                      <span className="text-gray-700">{det.detalhes.observacoes}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Fazendas + Dados Bancários */}
                                <div className="space-y-4">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                      Fazendas ({(det.fazendas || []).length})
                                    </p>
                                    {(det.fazendas || []).length === 0 && <p className="text-sm text-gray-400">Nenhuma fazenda</p>}
                                    <div className="space-y-2">
                                      {(det.fazendas || []).map((f, fi) => (
                                        <div key={fi} className="bg-white rounded-lg border border-emerald-100 px-3 py-2 text-sm">
                                          <p className="font-medium text-emerald-800">{f.nome}</p>
                                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                                            {f.inscricao_estadual && <span>IE: {f.inscricao_estadual}</span>}
                                            {f.endereco && <span>End: {f.endereco}{f.numero ? `, ${f.numero}` : ''}</span>}
                                            {f.cidade && <span>{f.cidade}{f.estado ? ` - ${f.estado}` : ''}</span>}
                                            {f.cep && <span>CEP: {f.cep}</span>}
                                            {f.telefone && <span>Tel: {f.telefone}</span>}
                                            {f.observacoes && <span className="col-span-2">Obs: {f.observacoes}</span>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                      Dados Bancários ({(det.dados_bancarios || []).length})
                                    </p>
                                    {(det.dados_bancarios || []).length === 0 && (
                                      <p className="text-sm text-gray-400">Nenhum dado bancário encontrado</p>
                                    )}
                                    <div className="space-y-2">
                                      {(det.dados_bancarios || []).map((b, bi) => (
                                        <div key={bi} className="bg-white rounded-lg border border-blue-100 px-3 py-2 text-sm">
                                          <p className="font-medium text-blue-800">{b.banco}</p>
                                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                                            {b.agencia && <span>Agência: {b.agencia}</span>}
                                            {b.conta && <span>Conta: {b.conta}</span>}
                                            {b.tipo_conta && <span>Tipo: {b.tipo_conta}</span>}
                                            {b.pix && <span className="col-span-2">PIX: {b.pix}</span>}
                                            {b.is_principal && <span className="text-blue-600 font-medium">Principal</span>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
                {filtrados.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Nenhum cliente encontrado</td></tr>
                )}
              </tbody>
            </table>

            {/* Paginação */}
            {totalPaginas > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm">
                <span className="text-gray-500">
                  {(paginaAtual - 1) * PER_PAGE + 1}–{Math.min(paginaAtual * PER_PAGE, filtrados.length)} de {filtrados.length}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPagina(1)} disabled={paginaAtual === 1}
                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-200 transition-colors">«</button>
                  <button onClick={() => setPagina(p => p - 1)} disabled={paginaAtual === 1}
                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-200 transition-colors">‹</button>
                  {Array.from({ length: Math.min(7, totalPaginas) }, (_, i) => {
                    const p = totalPaginas <= 7 ? i + 1
                      : paginaAtual <= 4 ? i + 1
                      : paginaAtual >= totalPaginas - 3 ? totalPaginas - 6 + i
                      : paginaAtual - 3 + i
                    return (
                      <button key={p} onClick={() => setPagina(p)}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-colors
                          ${p === paginaAtual ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-600'}`}>
                        {p}
                      </button>
                    )
                  })}
                  <button onClick={() => setPagina(p => p + 1)} disabled={paginaAtual === totalPaginas}
                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-200 transition-colors">›</button>
                  <button onClick={() => setPagina(totalPaginas)} disabled={paginaAtual === totalPaginas}
                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-200 transition-colors">»</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Passo 2: Importar ── */}
      {step === 2 && (
        <div className="max-w-2xl space-y-5">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="font-semibold text-gray-800 mb-3">Selecionar Organizador</h2>
            {loadingOrg ? (
              <p className="text-sm text-gray-500">Carregando organizadores...</p>
            ) : (
              <>
                <input type="text" value={buscaOrg} onChange={e => setBuscaOrg(e.target.value)}
                  placeholder="Buscar organizador..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus />
                <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                  {organizadores
                    .filter(o => !buscaOrg || o.name?.toLowerCase().includes(buscaOrg.toLowerCase()))
                    .map(org => (
                      <button key={org.id}
                        onClick={() => { setOrgSelecionada(org); setResultado(null); setDryRunOk(false) }}
                        className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors flex items-center justify-between
                          ${orgSelecionada?.id === org.id
                            ? 'border-blue-500 bg-blue-50 text-blue-800 font-medium'
                            : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50 text-gray-700'}`}>
                        <span>{org.name}</span>
                        {orgSelecionada?.id === org.id && <span className="text-blue-500 text-xs font-semibold">✓ Selecionado</span>}
                      </button>
                    ))}
                  {organizadores.filter(o => !buscaOrg || o.name?.toLowerCase().includes(buscaOrg.toLowerCase())).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">Nenhum organizador encontrado</p>
                  )}
                </div>
              </>
            )}
          </div>

          {orgSelecionada && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Organização selecionada:</p>
                  <p className="font-semibold text-gray-900">{orgSelecionada.name}</p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>{clientes.length} clientes · busca detalhes de cada um durante importação</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={atualizarExistentes}
                  onChange={e => { setAtualizarExistentes(e.target.checked); setDryRunOk(false); setResultado(null) }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Atualizar clientes que já existem (dados cadastrais + fazendas/bancos novos)
              </label>
              <div className="flex gap-3">
                <button onClick={handleDryRun} disabled={loading}
                  className="flex-1 py-2.5 border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
                  {loading && !dryRunOk ? '⏳ Validando...' : '🔍 Validar (Dry-Run)'}
                </button>
                <button onClick={handleImportar} disabled={loading || !dryRunOk}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
                  {loading && jobId
                    ? (jobFase === 'buscando' ? '⏳ Buscando clientes...'
                      : jobFase === 'scraping' ? `⏳ Raspando ${jobProgresso.concluidos}/${jobProgresso.total}...`
                      : jobFase === 'salvando' ? '⏳ Salvando no banco...'
                      : '⏳ Aguarde...')
                    : '🚀 Importar'}
                </button>
              </div>
              {!dryRunOk && <p className="text-xs text-gray-400 text-center">Execute o dry-run antes de importar</p>}
            </div>
          )}

          {/* Progresso do job em background */}
          {loading && jobId && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-3">
                <svg className="animate-spin h-5 w-5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                <span className="font-medium text-blue-800 text-sm">{jobFaseLabel || 'Processando...'}</span>
              </div>

              {/* Fases */}
              <div className="flex gap-2">
                {['buscando', 'scraping', 'salvando'].map((f, i) => {
                  const fases = ['buscando', 'scraping', 'salvando']
                  const atual = fases.indexOf(jobFase)
                  const idx   = fases.indexOf(f)
                  return (
                    <span key={f} className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all
                      ${jobFase === f ? 'bg-blue-600 text-white' :
                        atual > idx ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                      {i + 1}. {f === 'buscando' ? 'Buscando lista' : f === 'scraping' ? 'Raspando detalhes' : 'Salvando no banco'}
                    </span>
                  )
                })}
              </div>

              {/* Barra de progresso do scraping */}
              {jobFase === 'scraping' && jobProgresso.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-blue-600 mb-1">
                    <span>{jobProgresso.concluidos} de {jobProgresso.total} clientes raspados</span>
                    <span>{Math.round(jobProgresso.concluidos / jobProgresso.total * 100)}%</span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.round(jobProgresso.concluidos / jobProgresso.total * 100)}%` }} />
                  </div>
                </div>
              )}

              <p className="text-xs text-blue-500">Pode aguardar — o processo continua mesmo se fechar a tela</p>
            </div>
          )}

          {resultado && (
            <div className={`border rounded-xl p-5 ${resultado.dry_run ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <h3 className={`font-semibold mb-3 ${resultado.dry_run ? 'text-blue-800' : 'text-emerald-800'}`}>
                {resultado.dry_run ? '🔍 Resultado da Validação' : '✅ Importação Concluída'}
              </h3>
              <div className={`grid gap-4 text-sm ${resultado.dry_run ? (atualizarExistentes ? 'grid-cols-4' : 'grid-cols-3') : (atualizarExistentes ? 'grid-cols-6' : 'grid-cols-5')}`}>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">{resultado.total_scraped}</div>
                  <div className="text-gray-500">Total sistema antigo</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${resultado.dry_run ? 'text-blue-700' : 'text-emerald-700'}`}>{resultado.importados}</div>
                  <div className="text-gray-500">{resultado.dry_run ? 'Serão importados' : 'Importados'}</div>
                </div>
                {atualizarExistentes ? (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-600">{resultado.atualizados ?? 0}</div>
                    <div className="text-gray-500">{resultado.dry_run ? 'Serão atualizados' : 'Atualizados'}</div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600">{resultado.duplicatas}</div>
                    <div className="text-gray-500">Já existem (pulados)</div>
                  </div>
                )}
                {!resultado.dry_run && (
                  <>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{resultado.fazendas_importadas ?? 0}</div>
                      <div className="text-gray-500">Fazendas</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{resultado.bancos_importados ?? 0}</div>
                      <div className="text-gray-500">Dados Bancários</div>
                    </div>
                  </>
                )}
              </div>
              {resultado.erros?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-red-700 mb-1">Erros ({resultado.erros.length}):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {resultado.erros.map((e, i) => (
                      <p key={i} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{e}</p>
                    ))}
                  </div>
                </div>
              )}
              {!resultado.dry_run && (resultado.importados > 0 || resultado.atualizados > 0) && (
                <div className="mt-4 p-3 bg-emerald-100 rounded-lg text-sm text-emerald-800 text-center font-medium">
                  {resultado.importados} cliente(s) novo(s){atualizarExistentes ? `, ${resultado.atualizados ?? 0} atualizado(s)` : ''}, {resultado.fazendas_importadas ?? 0} fazenda(s) e {resultado.bancos_importados ?? 0} dado(s) bancário(s) para {orgSelecionada?.name}!
                </div>
              )}
            </div>
          )}

          <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700 underline">
            ← Voltar para revisão
          </button>
        </div>
      )}
    </div>
  )
}
